/*
 * Formular-Vorbefuellung einer Browser-Kachel (Rezept `inhalt.ausfuellen`, Regeln und
 * Skript in shared/vorbefuellung.ts). Laeuft bei JEDEM Laden der Seite erneut — dasselbe
 * Takt-Muster wie PartnerKontoAutoLogin (dom-ready/did-navigate), im <iframe>-Pfad am
 * `load`. Gelauscht wird in der CAPTURE-Phase am Panel-Container: dort kommen die
 * Ereignisse jeder Webview/jedes Iframes darunter vorbei, auch wenn das Element nach
 * einem Geraetewechsel neu entsteht.
 *
 * Abgeschickt wird NIE. Jedes nicht gefundene Feld steht sichtbar am Panel (gleiche
 * Regel wie AppLoginPanel: nie still schlucken).
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  ausfuellFehler, baueFuellSkript, fuellZusammenfassung, loeseAuf,
  type AusfuellRezept, type FuellErgebnis, type ProfilWerte,
} from '../../../shared/vorbefuellung';

/** Seiten bauen Formulare oft erst nach dem Laden per JS — darum mehrere Anlaeufe. */
const ANLAEUFE_MS = [300, 1500, 4000, 8000];

interface Props {
  rezept: unknown;
  isElectron: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  webviewRef: RefObject<HTMLWebViewElement | null>;
  iframeRef: RefObject<HTMLIFrameElement | null>;
}

type Stand = { art: 'laedt' | 'ok' | 'fehler'; text: string };

export default function Vorbefuellung({ rezept, isElectron, containerRef, webviewRef, iframeRef }: Props) {
  const formFehler = ausfuellFehler(rezept);
  const r = formFehler ? null : (rezept as AusfuellRezept);
  const [profil, setProfil] = useState<ProfilWerte | null>(null);
  const [stand, setStand] = useState<Stand>({ art: 'laedt', text: 'Profil wird geladen …' });
  const [takt, setTakt] = useState(0);
  const lauf = useRef(0);

  useEffect(() => {
    if (!r) return;
    let ab = false;
    setProfil(null);
    fetch(`/api/guide/vorbefuellung/profil/${encodeURIComponent(r.profil)}`, { cache: 'no-store' })
      .then(async res => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error ?? `HTTP ${res.status}`);
        return d.werte as ProfilWerte;
      })
      .then(w => { if (!ab) setProfil(w); })
      .catch(e => { if (!ab) setStand({ art: 'fehler', text: `Profil „${r.profil}“ nicht ladbar: ${e instanceof Error ? e.message : e}` }); });
    return () => { ab = true; };
  }, [r?.profil]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const tick = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.tagName === 'WEBVIEW' || t?.tagName === 'IFRAME') setTakt(n => n + 1);
    };
    const arten = isElectron ? ['dom-ready', 'did-navigate'] : ['load'];
    for (const a of arten) el.addEventListener(a, tick, true);
    return () => { for (const a of arten) el.removeEventListener(a, tick, true); };
  }, [containerRef, isElectron]);

  const rezeptSchluessel = JSON.stringify(rezept);
  useEffect(() => {
    if (!r || !profil) return;
    const meinLauf = ++lauf.current;
    const { felder, fehlt: profilFehlt } = loeseAuf(r, profil);
    const skript = baueFuellSkript(felder);
    const timer: ReturnType<typeof setTimeout>[] = [];
    // Was ein frueherer Anlauf DIESES Ladens eingetragen hat, zaehlt als eingetragen — nicht als "schon befuellt".
    const selbstEingetragen = new Set<string>();
    const einmal = async (letzter: boolean): Promise<boolean> => {
      if (lauf.current !== meinLauf) return true;
      let roh: string;
      try {
        if (isElectron) {
          const wv = webviewRef.current as unknown as { executeJavaScript?: (c: string) => Promise<unknown> } | null;
          if (!wv?.executeJavaScript) return false;
          roh = String(await wv.executeJavaScript(`(() => {${skript}})()`));
        } else {
          const f = iframeRef.current;
          if (!f) return false;
          let win: (Window & { Function: FunctionConstructor }) | null;
          try { win = f.contentWindow as typeof win; void win?.document; } catch { win = null; }
          if (!win?.document) {
            setStand({ art: 'fehler', text: 'Fremde Seite (cross-origin) im Browser-Fenster — Vorbefüllung geht nur in der CUI-App (Electron), nicht im Web-Browser/iPad.' });
            return true;
          }
          if (win.document.readyState === 'loading') return false;
          roh = String(new win.Function(skript)());
        }
      } catch (e) {
        if (letzter) setStand({ art: 'fehler', text: `Vorbefüllung gescheitert: ${e instanceof Error ? e.message : e}` });
        return false;
      }
      if (lauf.current !== meinLauf) return true;
      const erg = JSON.parse(roh) as FuellErgebnis;
      for (const sel of [...erg.gefuellt, ...erg.gehakt]) selbstEingetragen.add(sel);
      const z = fuellZusammenfassung({ ...erg, gefuellt: [...selbstEingetragen], gehakt: [], schon: erg.schon.filter(sel => !selbstEingetragen.has(sel)) }, profilFehlt);
      // Fehlende Felder erst beim letzten Anlauf als Fehler zeigen — das Formular kann spaeter kommen.
      if (z.ok || letzter) setStand({ art: z.ok ? 'ok' : 'fehler', text: `Vorbefüllt (Profil ${r.profil}): ${z.text}` });
      else setStand({ art: 'laedt', text: `Vorbefüllung wartet auf das Formular … (${z.text})` });
      return z.ok;
    };
    let fertig = false;
    ANLAEUFE_MS.forEach((ms, i) => timer.push(setTimeout(async () => {
      if (fertig) return;
      fertig = await einmal(i === ANLAEUFE_MS.length - 1);
    }, ms)));
    return () => { timer.forEach(clearTimeout); };
  }, [profil, takt, rezeptSchluessel, isElectron]); // eslint-disable-line react-hooks/exhaustive-deps

  const zeige: Stand = formFehler ? { art: 'fehler', text: `Rezept für die Vorbefüllung ungültig: ${formFehler}` } : stand;
  const farbe = zeige.art === 'ok' ? 'var(--tn-green, #9ece6a)' : zeige.art === 'fehler' ? 'var(--tn-red, #f7768e)' : 'var(--tn-text-muted)';
  return (
    <div data-ai-id="browser-vorbefuellung" data-vorbefuellung={zeige.art} role={zeige.art === 'fehler' ? 'alert' : 'status'}
      title="Trägt Profilwerte in leere Felder ein, bei jedem Laden der Seite. Schickt nie etwas ab."
      style={{ fontSize: 11, lineHeight: '15px', padding: '2px 8px', borderTop: `1px solid ${farbe}`, color: farbe, background: 'var(--tn-bg-dark)', whiteSpace: 'normal', overflowWrap: 'anywhere', maxHeight: 64, overflowY: 'auto', flexShrink: 0 }}>
      ✎ {zeige.text}
    </div>
  );
}
